import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { getEnvironmentConfig } from '../utils/environment';
import { getAccessToken, isLoginAsSession } from 'utils/authData';
import {
  assertAuthStateMatchesRefreshedAccessToken,
  clearAuthAndRedirectAfterRefreshFailure,
  clearAuthAndRedirectToSignIn,
  getAuthSessionIdentity,
  getLatestAccessTokenAfterAuthChange,
  hasAuthSessionIdentityChanged,
  refreshAccessToken,
  resolveLoginAsRequestAuthFailure,
  shouldSkipRefresh,
} from 'utils/authRefresh';
import { createCorrelationRequestHeaders } from 'utils/observability/request-headers';

const { apiUrl } = getEnvironmentConfig();

const PUBLIC_AUTH_ENDPOINTS = new Set([
  'signIn',
  'signUp',
  'verifyEmail',
  'resendVerificationEmail',
  'sendPasswordReset',
  'passwordReset',
  'oneTimeEntry',
  'getShowPublicCabinetFileURL',
  'getPublicFolders',
  'getCostCodeFiles',
]);

function applyCorrelationHeaders(headers: Headers): void {
  const h = createCorrelationRequestHeaders();
  Object.entries(h).forEach(([k, v]) => headers.set(k, v));
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: apiUrl,
  prepareHeaders: (headers, { endpoint }) => {
    if (endpoint === 'getGeolocation' || endpoint === 'postFileToAWS') {
      return;
    }
    if (endpoint === 'getCountryRegions') {
      headers.set('x-rapidapi-host', 'wft-geo-db.p.rapidapi.com');
      headers.set('x-rapidapi-key', process.env.REACT_APP_RAPID_API_KEY || '');
      return;
    }

    if (PUBLIC_AUTH_ENDPOINTS.has(endpoint)) {
      applyCorrelationHeaders(headers);
      return headers;
    }

    headers.set('authorization', getAccessToken() ?? '');

    if (localStorage.getItem('company-id')) {
      headers.set('company-id', localStorage.getItem('company-id'));
      applyCorrelationHeaders(headers);
    }
  },
});

const getRestoredLoginAsError = (
  error: FetchBaseQueryError | undefined
): { error: FetchBaseQueryError } => {
  const data = error?.data;
  const restoredData =
    data && typeof data === 'object'
      ? { ...data, restoredLoginAsSession: true }
      : { originalData: data, restoredLoginAsSession: true };

  return {
    error: {
      status: 401,
      data: restoredData,
    },
  };
};

const baseQueryWithRefresh: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const wasLoginAsRequest = isLoginAsSession();
  const requestAccessToken = getAccessToken();
  const requestAuthSessionIdentity = getAuthSessionIdentity(requestAccessToken);
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status !== 401 || shouldSkipRefresh(args)) {
    return result;
  }

  if (wasLoginAsRequest) {
    resolveLoginAsRequestAuthFailure();
    return getRestoredLoginAsError(result.error);
  }

  if (hasAuthSessionIdentityChanged(requestAuthSessionIdentity)) {
    return result;
  }

  const latestAccessToken = getLatestAccessTokenAfterAuthChange(requestAccessToken);
  if (latestAccessToken) {
    const retryResult = await rawBaseQuery(args, api, extraOptions);

    if (retryResult.error?.status === 401) {
      if (hasAuthSessionIdentityChanged(requestAuthSessionIdentity)) {
        return retryResult;
      }

      const restoredLoginAsSession = clearAuthAndRedirectToSignIn();
      if (restoredLoginAsSession) {
        return getRestoredLoginAsError(retryResult.error);
      }
    }

    return retryResult;
  }

  try {
    const refreshedToken = await refreshAccessToken();
    assertAuthStateMatchesRefreshedAccessToken(refreshedToken);
    const retryResult = await rawBaseQuery(args, api, extraOptions);

    if (retryResult.error?.status === 401) {
      if (hasAuthSessionIdentityChanged(requestAuthSessionIdentity)) {
        return retryResult;
      }

      const restoredLoginAsSession = clearAuthAndRedirectToSignIn();
      if (restoredLoginAsSession) {
        return getRestoredLoginAsError(retryResult.error);
      }
    }

    return retryResult;
  } catch (error) {
    const restoredLoginAsSession = clearAuthAndRedirectAfterRefreshFailure(error);
    if (restoredLoginAsSession) {
      return getRestoredLoginAsError(result.error);
    }

    return result;
  }
};

export const api = createApi({
  baseQuery: baseQueryWithRefresh,
  keepUnusedDataFor: 10,
  tagTypes: [
    'Post Audience',
    'Specific Users',
    'User',
    'Company',
    'Template',
    'Projects',
    'Preference',
    'Current Plan',
    'Billing History',
    'Payment Plans',
    'Payment Methods',
    'Company Roles',
    'Profile Push Notifications',
    'Profile Email Notifications',
    'Company Push Notifications',
    'Company Email Notifications',
    'Invites',
    'Profiles',
    'Role Permissions',
    'Selection Category',
    'Selections Summary',
    'Project Cost Codes',
    'File Cabinet Folder',
    'File Cabinet',
    'Folder Permissions',
    'Default Cost Codes',
    'Default Cost Categories',
    'Checklists Templates',
    'Checklists Template Items',
    'Schedule Checklists',
    'Invite Existence',
    'Selections Templates',
    'Selections Template',
    'Unviewed Counts',
    'Selection Variant Types',
    'Selection Variant Library',
    'SelectionItem',
    'Project Cost Code Categories',
    'Schedule Templates Checklist',
    'Project File Cabinet Deleted Files',
  ],
  endpoints: () => ({}),
});
